import _ from 'lodash';
import {getFormattedDateForApiOrValue} from 'utils/datetime';
import {parseGetParams, getURLParams} from 'utils/urls';

type FilterValueForStore =
  | null
  | string
  | number
  | boolean
  | Array<string | number>;
type FilterValue = FilterValueForStore; // equal for now
type FilterValueForApi = Exclude<FilterValue, null>;
type FilterValueForStoreWithoutArrays = Exclude<
  FilterValueForStore,
  Array<string>
>;

export type Filter = {[key: string]: FilterValue};
type FilterForApi = {[key: string]: FilterValueForApi};
type FilterForUrl = {[key: string]: string};

export type FilterForStore = {[key: string]: FilterValueForStore};

/*
Removes arrays from the FilterValueForStore
*/
export const getFilterValue = (
  value: FilterValueForStore
): FilterValueForStoreWithoutArrays => {
  if (_.isArray(value) && _.isEmpty(value)) {
    return null;
  }

  if (_.isArray(value)) {
    return value.join(',');
  }

  return value;
};

export const transformFiltersForStoreToFiltersForApi = (
  filters: FilterForStore
): FilterForApi => {
  const dictWithoutNullKeysOrEmptyStrings = _.omitBy(
    transformFiltersForStoreToFilter(filters),
    x => _.isNull(x) || x === ''
  );

  // Transform filter values in api format
  const filtersWithFormattedDate: FilterForApi = _.mapValues(
    dictWithoutNullKeysOrEmptyStrings,
    getFormattedDateForApiOrValue
  );

  return filtersWithFormattedDate;
};

/*
  Transforms arrays into comma separated values
*/
export const transformFiltersForStoreToFilter = (
  filters: FilterForStore
): Filter => {
  return _.mapValues(filters, filter => getFilterValue(filter));
};

export const getUpdatedFilter = (
  newFilter: {field: string; value: string | number | boolean},
  storeFilter: FilterForStore
): {
  field: string;
  value: string | number | boolean | Array<string | number | boolean>;
} => {
  if (
    _.isEmpty(
      Object.keys(storeFilter).filter(
        fieldName => fieldName === newFilter.field
      )
    )
  ) {
    throw new Error(
      `The filter ${newFilter.field} is not in the intial filters`
    );
  }

  const storeValue = storeFilter[newFilter.field];

  // If the store value is an array -> check if the new value is a string since
  // we only work with arrays of string in the store
  if (_.isArray(storeValue) && typeof newFilter.value === 'string') {
    // exclude the value if in, include the value if not
    const newArrayValue = _.xor(storeValue, [newFilter.value]);

    return {field: newFilter.field, value: newArrayValue};
  }

  return {field: newFilter.field, value: newFilter.value};
};

/*
  Receives:
  - filters from url in this format:
    {key1: 'value1', key2: 'value2,value3', key3: 'true'}
  - initialState of filters

  Returns filters in following format:
  {
    key1: {value: 'value1'},
    key2: {value: ['value2', 'value3'}
    key3: {value: true}
  }
  => Where `key2` is defined as array filter in the initialState
*/
export const transformFiltersForUrlToFiltersFromStore = (
  urlFilters: FilterForUrl,
  initialFilters: FilterForStore
): FilterForStore => {
  const arrayFilters = _.keys(initialFilters).filter(key =>
    _.isArray(initialFilters[key])
  );

  const booleanFilters = _.keys(initialFilters).filter(key =>
    _.isBoolean(initialFilters[key])
  );

  // Checks the type of the filters from the storeFilters and casts them
  return _.mapValues(urlFilters, (value, key) => {
    if (_.includes(arrayFilters, key)) {
      return value.split(',');
    }

    if (_.includes(booleanFilters, key)) {
      return value === 'true';
    }

    return value;
  });
};

/*
  Pulls the filters from the URL GET parameters and casts them to FilterForStore
*/
export const getFiltersFromUrl = (
  initialFilters: FilterForStore
): FilterForStore => {
  const pageParams = parseGetParams(getURLParams());
  const urlFilters = _.pick(pageParams, _.keys(initialFilters));

  return transformFiltersForUrlToFiltersFromStore(urlFilters, initialFilters);
};
